import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IPlaybookBankAnnotated,
  PlaybookLeverType,
  PlaybookRisk,
  VariantType,
  playbookBankService,
  experimentService,
  merchandisingRulesService,
} from 'src/services';
import { useMountRef } from 'src/components-dummy/hooks/use-mount-ref';
import { SyteProductType } from 'src/services/src/service/types/constants/syte-product-types';
import { VariantFeature } from 'src/components-bl/Experiment/ExperimentForm/VariantPanel/constants';
import { getProductName } from 'src/components-bl/MerchandisingRules/merchandisingRules.helpers';
import { buildDefaultName, isAutoGeneratedName } from '../GoalStep/constants';
import { mapLeverTypeToVariantFeature } from '../wizard-lever-mapping';
import { BUILD_YOUR_OWN_RISK, RECOMMENDED_SPLIT } from '../wizard-risk-split';
import { resetLeverToControl } from '../VariantsStep/leverReset';
import { LEVER_OPTIONS, RISK_RANK } from './constants';
import { UseWhatToTestStepReturnType, WhatToTestMode, WhatToTestStepProps } from './types';

const VARIANT_FEATURE_TO_LEVER_TYPE: Partial<Record<VariantFeature, PlaybookLeverType>> = {
  [VariantFeature.RankingStrategy]: 'ranking_strategy',
  [VariantFeature.MerchandisingRules]: 'merchandising_rule',
  [VariantFeature.RecEnginesGeneralSettings]: 'view_config',
};

export const useWhatToTestStep = ({
  shopId,
  experiment,
  goalMetric,
}: WhatToTestStepProps): UseWhatToTestStepReturnType => {
  const wizardSelection = experiment?.wizardSelection;
  const firstLever = wizardSelection?.levers?.[0];

  const [mode, setMode] = useState<WhatToTestMode>(
    wizardSelection?.mode === 'build_your_own' ? 'build-your-own' : 'playbooks'
  );
  const [playbooks, setPlaybooks] = useState<IPlaybookBankAnnotated[]>([]);
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(true);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<string | undefined>(
    wizardSelection?.selectedPlaybookId
  );
  const [selectedSurface, setSelectedSurface] = useState<SyteProductType | undefined>(
    firstLever?.surface
  );
  const [selectedLever, setSelectedLever] = useState<PlaybookLeverType | undefined>(
    firstLever ? VARIANT_FEATURE_TO_LEVER_TYPE[firstLever.feature] : undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMerchandisingRules, setHasMerchandisingRules] = useState<boolean | undefined>(
    undefined
  );

  const mountRef = useMountRef();

  useEffect(() => {
    merchandisingRulesService
      .getRules({ shopId, skip: 0, limit: 1 })
      .then(result => {
        if (!mountRef.current.hasUnmounted)
          setHasMerchandisingRules(result.pagination.totalCount > 0);
      })
      .catch(() => {
        if (!mountRef.current.hasUnmounted) setHasMerchandisingRules(false);
      });
  }, [shopId, mountRef]);

  useEffect(() => {
    if (hasMerchandisingRules === false && selectedLever === 'merchandising_rule') {
      setSelectedLever(undefined);
    }
  }, [hasMerchandisingRules, selectedLever]);

  useEffect(() => {
    setIsLoadingPlaybooks(true);
    playbookBankService
      .getPlaybookBank({ shopId })
      .then(bank => {
        if (!mountRef.current.hasUnmounted) {
          setPlaybooks(bank);
        }
      })
      .finally(() => {
        if (!mountRef.current.hasUnmounted) {
          setIsLoadingPlaybooks(false);
        }
      });
  }, [shopId, mountRef]);

  const orderedPlaybooks = useMemo(() => {
    if (!Array.isArray(playbooks)) return [];
    return [...playbooks].sort((a, b) => {
      const aMatchesGoal = a.metric === goalMetric ? 0 : 1;
      const bMatchesGoal = b.metric === goalMetric ? 0 : 1;

      if (aMatchesGoal !== bMatchesGoal) return aMatchesGoal - bMatchesGoal;

      return RISK_RANK[a.risk] - RISK_RANK[b.risk];
    });
  }, [playbooks, goalMetric]);

  useEffect(() => {
    if (isLoadingPlaybooks || mode !== 'playbooks' || selectedPlaybookId) return;

    const firstAvailable = orderedPlaybooks.find(playbook => playbook.satisfied);
    if (firstAvailable) {
      setSelectedPlaybookId(firstAvailable.id);
    }
  }, [isLoadingPlaybooks, mode, selectedPlaybookId, orderedPlaybooks]);

  const selectPlaybook = (playbookId: string): void => {
    setSelectedPlaybookId(playbookId);
  };

  const selectSurface = (surface: SyteProductType): void => {
    setSelectedSurface(surface);
  };

  const selectLever = (lever: PlaybookLeverType): void => {
    setSelectedLever(lever);
  };

  const canContinue =
    mode === 'playbooks'
      ? !!selectedPlaybookId
      : !!selectedSurface &&
        !!selectedLever &&
        !(selectedLever === 'merchandising_rule' && hasMerchandisingRules === false);

  const onSubmit = useCallback((): Promise<boolean> => {
    if (!experiment || !canContinue) return Promise.resolve(false);

    let levers: { feature: VariantFeature; surface: SyteProductType }[] = [];
    let selectedPlaybookIdToSave: string | undefined;
    let risk: PlaybookRisk = BUILD_YOUR_OWN_RISK;
    let surfaceLabel: string | undefined;
    let leverSummary: string | undefined;

    if (mode === 'playbooks') {
      const playbook = playbooks.find(p => p.id === selectedPlaybookId);
      const feature = playbook && mapLeverTypeToVariantFeature(playbook.leverType);
      levers =
        playbook && feature ? [{ feature, surface: playbook.surface as SyteProductType }] : [];
      selectedPlaybookIdToSave = selectedPlaybookId;
      risk = playbook?.risk ?? BUILD_YOUR_OWN_RISK;
      if (playbook) {
        surfaceLabel = getProductName(playbook.surface as SyteProductType);
        leverSummary = playbook.name;
      }
    } else if (selectedSurface && selectedLever) {
      const feature = mapLeverTypeToVariantFeature(selectedLever);
      levers = feature ? [{ feature, surface: selectedSurface }] : [];
      surfaceLabel = getProductName(selectedSurface);
      leverSummary = LEVER_OPTIONS.find(lever => lever.leverType === selectedLever)?.label;
    }

    const nextName =
      surfaceLabel && leverSummary ? buildDefaultName(surfaceLabel, leverSummary) : undefined;
    const shouldUpdateName =
      !!nextName && (!experiment.name || isAutoGeneratedName(experiment.name));

    const split = RECOMMENDED_SPLIT[risk];
    const controlVariant = experiment.variants.find(
      variant => variant.type === VariantType.Control
    );
    const testVariant = experiment.variants.find(variant => variant.type === VariantType.Test);

    // Test variant only ever carries drift for the one lever that's actually still selected.
    const newLever = levers[0];
    const leverChanged =
      !!firstLever &&
      (!newLever ||
        firstLever.feature !== newLever.feature ||
        firstLever.surface !== newLever.surface);
    const resetPreviousLever =
      leverChanged && controlVariant && testVariant
        ? resetLeverToControl({
            shopId,
            feature: firstLever.feature,
            surface: firstLever.surface,
            controlVariantId: controlVariant.id,
            testVariantId: testVariant.id,
          }).catch(() => undefined)
        : Promise.resolve();

    setIsSubmitting(true);
    return resetPreviousLever
      .then(() =>
        experimentService.updateExperiment({
          shopId,
          experiment: {
            id: experiment.id,
            wizardSelection: {
              mode: mode === 'playbooks' ? 'playbooks' : 'build_your_own',
              selectedPlaybookId: selectedPlaybookIdToSave,
              levers,
            },
            ...(shouldUpdateName ? { name: nextName } : {}),
            ...(controlVariant && testVariant
              ? {
                  productionTrafficAllocation: split.production,
                  variants: experiment.variants.map(variant => ({
                    id: variant.id,
                    name: variant.name,
                    type: variant.type,
                    trafficAllocation:
                      variant.id === controlVariant.id
                        ? split.control
                        : variant.id === testVariant.id
                          ? split.variant
                          : variant.trafficAllocation,
                  })),
                }
              : {}),
          },
        })
      )
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    experiment,
    canContinue,
    mode,
    playbooks,
    selectedPlaybookId,
    selectedSurface,
    selectedLever,
    shopId,
  ]);

  return {
    mode,
    setMode,
    playbooks: orderedPlaybooks,
    isLoadingPlaybooks,
    selectedPlaybookId,
    selectPlaybook,
    selectedSurface,
    selectSurface,
    selectedLever,
    selectLever,
    hasMerchandisingRules,
    canContinue,
    isSubmitting,
    onSubmit,
  };
};
